<script lang="ts" setup>
import SalesLead from '@/classes/sales_classes/salesLead'
import { type State } from '@/store/store_types'
import { getNiceDateTime } from '@/utils/timeUtils'
import { computed, ref } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import OutlinedBtn from '../ui-elements/buttons/OutlinedBtn.vue'
import TextBtn from '../ui-elements/buttons/TextBtn.vue'

const { mobile } = useDisplay()
const store = useStore<State>()
const emit = defineEmits<{
  (e: 'view-lead', lead: SalesLead): void
  (e: 'contact-lead', lead: SalesLead): void
}>()

const notContactedLeads = computed(() => {
  return store.state.salesLeads.filter((lead) => lead.contacted === false)
})
const notContactedLeadsSearchQuery = ref('')
const filteredNotContactedLeads = computed(() =>
  filterLeads(notContactedLeads.value, notContactedLeadsSearchQuery.value),
)

const contactedLeads = computed(() => {
  return store.state.salesLeads.filter((lead) => lead.contacted === true)
})
const contactedLeadsSearchQuery = ref('')
const filteredContactedLeads = computed(() =>
  filterLeads(contactedLeads.value, contactedLeadsSearchQuery.value),
)

function filterLeads(leads: SalesLead[], searchQuery: string) {
  if (!searchQuery || searchQuery === '' || searchQuery === ' ') {
    return leads
  } else {
    return leads.filter((lead) => {
      return !!(
        lead.user?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) || // users first name
        lead.user?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) || // users last name
        lead.user?.email?.toLowerCase().includes(searchQuery.toLowerCase()) || // users email
        lead.user?.directusId?.toString().includes(searchQuery.toLowerCase()) || // users directus id
        lead.trip?.name?.toLowerCase().includes(searchQuery.toLowerCase()) || // trip name
        lead.trip?.locations?.[0].name?.toLowerCase().includes(searchQuery.toLowerCase()) || // trip origin name
        lead.trip?.locations?.[lead.trip?.locations?.length - 1].name
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) || // trip destination name
        lead.trip?.vehicle?.name?.toLowerCase().includes(searchQuery.toLowerCase()) || // trip vehicle name
        lead.trip?.vehicle?.evModel?.name.toLowerCase().includes(searchQuery.toLowerCase()) || // trip vehicle ev model
        lead.trip?.vehicle?.directusId?.toString().includes(searchQuery.toLowerCase()) // trip vehicle directus id
      )
    })
  }
}

function formatDate(date: Date) {
  return getNiceDateTime(date)
}
</script>
<template>
  <v-card flat color="transparent" class="mt-5">
    <v-card
      v-if="notContactedLeads.length > 0"
      flat
      color="pwt_azure_20"
      class="mb-5 pa-4 d-flex align-center"
    >
      <v-icon color="pwt_palatinate_blue" class="mr-2"> mdi-bell-badge-outline </v-icon>
      <span>{{ notContactedLeads.length }} enquiries waiting for response</span>
    </v-card>
    <v-card flat color="transparent">
      <!-- mobile view -->
      <v-card-title v-if="mobile" class="d-flex flex-column justify-center align-start">
        <span> Enquiries waiting for response </span>
        <v-row no-gutters class="mt-2 flex-nowrap">
          <OutlinedBtn class="mr-2"> Sort by </OutlinedBtn>
          <OutlinedBtn> Filter </OutlinedBtn>
        </v-row>
      </v-card-title>
      <!-- desktop view -->
      <v-card-title v-else class="d-flex justify-start align-center">
        <span> Enquiries waiting for response </span>
        <v-spacer></v-spacer>
        <TextBtn> Sort by </TextBtn>
        <TextBtn> Filter </TextBtn>
      </v-card-title>
      <v-card-text>
        <v-text-field v-model="notContactedLeadsSearchQuery" clearable placeholder="Search..." />
        <v-row no-gutters v-if="filteredNotContactedLeads.length > 0">
          <v-card
            flat
            v-for="lead in filteredNotContactedLeads"
            :key="lead.local_id"
            :width="mobile ? '100%' : 'calc(50% - 8px)'"
            min-width="200px"
            rounded="lg"
            class="mr-0 mr-md-2 mb-2 d-flex justify-space-between align-center"
            color="pwt_gainsboro"
            @click="emit('view-lead', lead)"
          >
            <div class="flex-grow-1 flex-shrink-1" style="max-width: 100%">
              <v-card-title class="pb-0">
                Enquiry from {{ lead.user?.firstName ?? '' }} {{ lead.user?.lastName ?? '' }}
              </v-card-title>
              <v-card-subtitle class="mb-4 text-truncate">
                {{
                  lead.trip
                    ? 'Trip to ' + (lead.trip?.destination?.name ?? 'unknown location') + ' | '
                    : ''
                }}{{ lead.dateCreated ? formatDate(lead.dateCreated) : 'unknown date' }}
              </v-card-subtitle>
            </div>
            <v-btn
              color="pwt_snow"
              icon
              class="flex-shrink-0 flex-grow-0 ma-2"
              @click.stop="emit('contact-lead', lead)"
            >
              <v-icon color="pwt_azure"> mdi-email-outline </v-icon>
            </v-btn>
          </v-card>
        </v-row>
        <v-row v-else-if="!!notContactedLeadsSearchQuery" no-gutters>
          No enquiries waiting for response with your search query
        </v-row>
        <v-row v-else no-gutters> No enquiries waiting for response </v-row>
      </v-card-text>
    </v-card>
    <v-card flat color="transparent">
      <v-card-title v-if="mobile" class="d-flex flex-column justify-center align-start">
        <span> Followed up enquires </span>
        <v-row no-gutters class="mt-2 flex-nowrap">
          <OutlinedBtn class="mr-2"> Sort by </OutlinedBtn>
          <OutlinedBtn> Filter </OutlinedBtn>
        </v-row>
      </v-card-title>
      <v-card-title v-else class="d-flex justify-start align-center">
        <span> Followed up enquires </span>
        <v-spacer></v-spacer>
        <TextBtn> Sort by </TextBtn>
        <TextBtn> Filter </TextBtn>
      </v-card-title>
      <v-card-text>
        <v-text-field v-model="contactedLeadsSearchQuery" clearable placeholder="Search..." />
        <v-row no-gutters v-if="filteredContactedLeads.length > 0">
          <v-card
            flat
            v-for="lead in filteredContactedLeads"
            :key="lead.local_id"
            :width="mobile ? '100%' : 'calc(50% - 8px)'"
            min-width="200px"
            rounded="lg"
            class="mr-2 mb-2 d-flex justify-space-between align-center"
            color="pwt_gainsboro"
            @click="emit('view-lead', lead)"
          >
            <div class="flex-grow-1 flex-shrink-1" style="max-width: 100%">
              <v-card-title class="pb-0">
                Enquiry from {{ lead.user?.fullName ?? 'anonymous' }}
              </v-card-title>
              <v-card-subtitle class="mb-4">
                {{
                  lead.trip
                    ? 'Trip to ' + (lead.trip?.destination?.name ?? 'unknown location') + ' | '
                    : ''
                }}{{
                  lead.contactedBy ? 'Followed up by ' + lead.contactedBy.firstName : 'unknown rep'
                }}
              </v-card-subtitle>
            </div>
            <v-avatar color="primary" class="mr-3 mt-1">
              <img
                v-if="lead.contactedBy?.driver?.profilePicSrc"
                :src="lead.contactedBy?.driver?.profilePicSrc"
                width="100%"
                height="auto"
              />
              <v-icon v-else dark> mdi-account-circle </v-icon>
            </v-avatar>
          </v-card>
        </v-row>
        <v-row v-else-if="!!notContactedLeadsSearchQuery" no-gutters>
          No enquiries waiting for response with your search query
        </v-row>
        <v-row v-else no-gutters> No enquiries waiting for response </v-row>
      </v-card-text>
    </v-card>
  </v-card>
</template>
